/**
 * CateringTeamPicker — replaces the per-person staff selector for the
 * "Catering Team" tile inside Instant Pricing.
 *
 * UX:
 *   1. Customer enters guest count + menu-item count.
 *   2. We hit POST /api/catering-teams/quote → list of teams w/ matched
 *      cell price (snapped UP to the smallest bucket >= request),
 *      sorted cheapest-first by the server.
 *   3. Customer taps a card → addToCart with the snapped price.
 *   4. Teams whose max bucket is too small show "Contact for custom quote"
 *      and are placed last.
 *
 * Prices are all-inclusive (Cooking + Serving + Cleanup + Transport
 * within city) — we surface this prominently above the picker so the
 * customer knows the number won't grow at checkout.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CookingPot, ForkKnife, Broom, Truck, MapPin, Check, Spinner, MagnifyingGlass,
} from '@phosphor-icons/react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INCLUDED = [
  { icon: CookingPot, label: 'Cooking' },
  { icon: ForkKnife,  label: 'Serving' },
  { icon: Broom,      label: 'Cleanup' },
  { icon: Truck,      label: 'Transport' },
];

const CART_ID_PREFIX = 'catering-team-';

const CateringTeamPicker = ({ initialGuests, city, cart, addToCart, removeFromCart }) => {
  const [guests, setGuests] = useState(initialGuests || '');
  const [items, setItems] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const runQuote = async () => {
    const g = parseInt(guests, 10);
    const i = parseInt(items, 10);
    if (!g || g <= 0) { toast.error('Enter number of guests'); return; }
    if (!i || i <= 0) { toast.error('Enter number of menu items'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/catering-teams/quote`,
        { guests: g, items: i, city: city || undefined },
      );
      setResults(data);
      if ((data?.results || []).length === 0) {
        toast.info('No catering teams listed yet for this city. Try a wider area.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not fetch quotes');
    }
    setBusy(false);
  };

  const cartIdFor = (id) => `${CART_ID_PREFIX}${id}`;

  const toggleCart = (team) => {
    const id = cartIdFor(team.id);
    const already = cart.find((c) => c.id === id);
    if (already) {
      removeFromCart(id);
      return;
    }
    if (!team.price) {
      toast.info('Contact this team for a custom quote (your request exceeds their published bucket).');
      return;
    }
    addToCart({
      id,
      name: `${team.business_name} — ${team.snapped_guests_bucket} guests × ${team.snapped_items_bucket} items (all-in)`,
      price: team.price,
      category: 'Catering Team',
    });
  };

  return (
    <div className="space-y-3" data-testid="catering-team-picker">
      {/* Includes banner — match the editor wording */}
      <div className="rounded border-2 border-emerald-300 bg-emerald-50 p-2.5"
        data-testid="catering-includes-banner">
        <div className="text-xs font-bold text-emerald-900 mb-1.5">
          Prices below include everything — no surprises at checkout:
        </div>
        <div className="flex flex-wrap gap-1.5">
          {INCLUDED.map(({ icon: Icon, label }) => (
            <span key={label}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-emerald-300 text-[11px] font-semibold text-emerald-800">
              <Icon size={11} weight="fill" />
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* Inputs */}
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wide text-text-secondary mb-1">
            Guests
          </label>
          <input
            type="number"
            min="1"
            value={guests}
            onChange={(e) => setGuests(e.target.value)}
            placeholder="e.g. 200"
            className="w-28 px-2 py-1.5 text-sm border-2 border-[#E8E1D5] rounded focus:border-amber-500 focus:outline-none"
            data-testid="catering-picker-guests"
          />
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wide text-text-secondary mb-1">
            Menu items
          </label>
          <input
            type="number"
            min="1"
            value={items}
            onChange={(e) => setItems(e.target.value)}
            placeholder="e.g. 10"
            className="w-28 px-2 py-1.5 text-sm border-2 border-[#E8E1D5] rounded focus:border-amber-500 focus:outline-none"
            data-testid="catering-picker-items"
          />
        </div>
        <Button onClick={runQuote} disabled={busy} size="sm"
          className="bg-amber-600 hover:bg-amber-700 h-[34px]"
          data-testid="catering-picker-quote-btn">
          {busy
            ? <Spinner size={14} className="animate-spin mr-2" />
            : <MagnifyingGlass size={14} className="mr-2" weight="bold" />}
          Get Quotes
        </Button>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-2" data-testid="catering-picker-results">
          {results.results.length === 0 ? (
            <div className="text-xs text-text-secondary border border-dashed border-[#E8E1D5] rounded p-3 text-center">
              No catering teams yet. Check back soon.
            </div>
          ) : (
            results.results.map((team) => {
              const cartId = cartIdFor(team.id);
              const inCart = cart.find((c) => c.id === cartId);
              const noPrice = !team.price;
              return (
                <div
                  key={team.id}
                  onClick={() => toggleCart(team)}
                  className={`p-3 border-2 rounded transition-all cursor-pointer ${
                    inCart
                      ? 'border-green-500 bg-green-50/40'
                      : noPrice
                      ? 'border-[#E8E1D5] bg-[#FAF6EE]/30'
                      : 'border-[#E8E1D5] hover:border-amber-400'
                  }`}
                  data-testid={`catering-team-result-${team.id}`}
                >
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{team.business_name}</span>
                        {team.location && (
                          <span className="text-[11px] text-text-secondary inline-flex items-center gap-0.5">
                            <MapPin size={11} weight="fill" /> {team.location}
                          </span>
                        )}
                        {inCart && <Check size={14} className="text-green-600" weight="bold" />}
                      </div>
                      <div className="text-[11px] text-text-secondary mt-0.5">
                        {noPrice
                          ? `Request (${results.requested_guests}g × ${results.requested_items}i) exceeds published bucket`
                          : `Snapped to ${team.snapped_guests_bucket} guests × ${team.snapped_items_bucket} items`}
                      </div>
                    </div>
                    <div className="text-right">
                      {noPrice ? (
                        <span className="text-[11px] font-bold text-orange-700 bg-orange-100 border border-orange-200 px-2 py-0.5 rounded uppercase tracking-wide">
                          Contact for custom quote
                        </span>
                      ) : (
                        <span className="text-base font-bold text-amber-700">
                          ₹{team.price.toLocaleString('en-IN')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default CateringTeamPicker;
